import { ReactNode } from 'react'

import useTranslation from 'next-translate/useTranslation'

import {
	LOADING_MESSAGE_TIMEOUT,
	LONGER_THAN_EXPECTED_TIMEOUT,
	REFERRAL_COOKIE
} from '@/modules/trials/constants'
import { CreateStoreErrorSchema, GetStoreApiResponse } from '@/modules/trials/interfaces'
import * as Sentry from '@sentry/nextjs'
import Cookies from 'js-cookie'
import { FieldValues, SubmitHandler } from 'react-hook-form'

import { sendDataLayerEvent } from '@/utils/analytics/events'
import { shouldRedirectToSamlLogin } from '@/utils/saml/redirectToSamlLogin'

import { EHawkTalonLoadScript } from '@/components/Scripts/Talon'

import { TrialCui } from '../TrialCui'
import { TrialError } from '../TrialError'
import { TrialExistingAccount } from '../TrialExistingAccount'
import { TrialFields } from '../TrialFields'
import { TrialLoading } from '../TrialLoading'
import { useTrial } from '../TrialProvider'

type Props = {
	switchToEnterpriseSku: boolean
	loadingText: string
	form1CTAText: string
	form2CTAText: string
	legalText: ReactNode
}

export const closeTrialFlow = (storeLoginUrl: string) => {
	// Redirect to trial store
	const loginForm = document.createElement('form')
	loginForm.setAttribute('method', 'post')
	loginForm.setAttribute('action', storeLoginUrl)
	document.getElementsByTagName('body')[0].appendChild(loginForm)

	loginForm.submit()
}

export function TrialFormStatus({
	switchToEnterpriseSku,
	loadingText,
	form1CTAText,
	form2CTAText,
	legalText
}: Props) {
	const {
		skuValue,
		currentStep,
		isLoading,
		showRedirectMessage,
		showCuiMessage,
		setShowCuiMessage,
		showError,
		setLoadingMessage,
		setIsLoading,
		setStoreInfo,
		setStoreLoginUrl,
		setErrorMsg,
		setCurrentStep,
		setTalon,
		csrfToken,
		storeInfo,
		storeLoginUrl,
		setShowRedirectMessage,
		talon,
		recaptchaId,
		setRecaptchaError,
		setEmail,
		values,
		setValues,
		setDisableButton,
		pollingService,
		resetRecaptcha,
		sendTransactionEvent,
		loadingSecondaryText,
		authenticationService,
		setIsAccountAvailable
	} = useTrial()

	const { t } = useTranslation('trials')

	const handleOnSubmitStep1: SubmitHandler<FieldValues> = async (formValues) => {
		setIsLoading(true)

		try {
			setLoadingMessage('')

			const shouldLoadRecaptcha =
				window?.navigator?.userAgent !== process.env.NEXT_PUBLIC_TRIALS_FORM_USER_AGENT

			let token = null

			if (shouldLoadRecaptcha) {
				token = grecaptcha.enterprise.getResponse(recaptchaId)
				setRecaptchaError(!token)
			}

			if (shouldLoadRecaptcha && !token) {
				return
			}

			const body = {
				...formValues,
				email: formValues.email,
				plan_sku: skuValue,
				_csrf: csrfToken,
				talon,
				referral_id: Cookies.get(REFERRAL_COOKIE) ?? '',
				token,
				switch_to_enterprise_sku: switchToEnterpriseSku
			}

			setValues({
				...body
			})

			setEmail(formValues.email)

			setErrorMsg('')

			setTimeout(() => setLoadingMessage(loadingSecondaryText ?? ''), LOADING_MESSAGE_TIMEOUT)
			setTimeout(
				() => setLoadingMessage(t('trialForm.loading.longerThanExpected')),
				LONGER_THAN_EXPECTED_TIMEOUT
			)

			sendDataLayerEvent('e_optionsDropDown', {
				projectedAnnualRevenue: formValues.projectedAnnualRevenue
			})

			// Check if the account is available
			const isAccountAvailableResponse = await fetch('/api/trials/verify-account-availability/', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(body)
			})

			// If the response status is 200 then the account is available
			if (isAccountAvailableResponse.status === 200) {
				setIsAccountAvailable(true)
			} else {
				setIsAccountAvailable(false)
			}

			// Create the store
			const response = await fetch('/api/trials/create-store/', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(body)
			})

			authenticationService.setSessionCookie({
				email: body.email,
				newAccount: true,
				storeUuid: ''
			})

			resetRecaptcha()
			setErrorMsg('')
			setLoadingMessage('')

			switch (response.status) {
				case 200:
					// If the response status is 200 then the store is created
					const data = await response.json()

					authenticationService.setSessionCookie({
						email: body.email,
						newAccount: true,
						storeUuid: data.storeUuid
					})

					const pollServiceData: GetStoreApiResponse = await pollingService.pollStoreApi()
					setStoreInfo({
						storeId: pollServiceData.storeId,
						userEmail: pollServiceData.emailAddress
					})
					setStoreLoginUrl(pollServiceData.storeUrl)

					sendTransactionEvent(pollServiceData)

					// Show optional questions
					setCurrentStep(2)
					setIsLoading(false)
					setLoadingMessage('')

					setCurrentStep(2)

					break
				case 409:
					// If the response status is 409 then the email belongs to an account and we will redirect to the SAML service for authentication
					if (shouldRedirectToSamlLogin(body.email)) {
						setShowRedirectMessage(true)

						return
					}

					throw new Error('SAML_SERVICE not set')

				case 403:
					setShowCuiMessage(true)
					setIsLoading(false)
					break
				case 422:
				case 427:
					setErrorMsg(t('trialForm.status.errors.427'))
					setIsLoading(false)
					setLoadingMessage('')
					break
				case 500:
					setErrorMsg(t('trialForm.status.errors.default'))
					setIsLoading(false)
					setLoadingMessage('')
					break
				case 524:
					setErrorMsg(t('trialForm.loading.longerThanExpected'))
					setIsLoading(false)
					setLoadingMessage('')
					break
				default:
					setLoadingMessage('')
					setLoadingMessage('')
					return response.text().then((res) => {
						let json

						try {
							json = JSON.parse(res)
						} catch {
							throw new Error(`Unexpected response (${response.status}): ${res}`)
						}

						throw json
					})
			}
		} catch (err) {
			resetRecaptcha()

			const errors = CreateStoreErrorSchema.safeParse(err)

			// Even though errors is an array, we are just using the first error for now
			// This should be updated to handle multiple errors in the future
			const errorMessage = errors.success ? errors.data[0] : t('trialForm.status.errors.default')

			setErrorMsg(errorMessage)

			if (err instanceof Error) {
				Sentry.captureException(new Error(`Error creating trial: ${err.message}`, { cause: err }))

				return
			}

			Sentry.captureException(
				new Error(`Error creating trial: ${JSON.stringify(err)}`, { cause: err })
			)
		} finally {
			setIsLoading(false)
		}
	}

	const handleOnSubmitStep2: SubmitHandler<FieldValues> = async (formValues) => {
		setDisableButton(true)
		try {
			const body = {
				...values,
				currentWebsite: formValues.currentWebsite,
				currentWebsiteUrl: formValues.currentWebsiteUrl,
				industry: formValues.industry,
				experience: formValues.experience
			}

			const response = await fetch('/api/trials/optional-fields/', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					...storeInfo,
					formData: {
						...body
					}
				})
			})

			if (!response.ok) {
				throw new Error()
			}

			response
				.json()
				.then(() => closeTrialFlow(storeLoginUrl))
				.catch((e) => {
					throw e
				})

			setDisableButton(false)
		} catch (err) {
			setErrorMsg(t('trialForm.status.errors.default'))
			setDisableButton(false)
		}
	}

	return (
		<div
			className={
				'flex flex-col gap-6 bg-white px-6 pb-8 lg:px-12 lg:pt-3 lg:pb-14 shadow-md w-full'
			}
		>
			{showError && <TrialError />}

			{!isLoading && !showRedirectMessage && !showCuiMessage && (
				<>
					{currentStep === 2 && (
						<div className="flex flex-col gap-6 text-center pt-8 lg:pt-12">
							<h2 className="text-3xl font-medium">{t('trialForm.headings.step2.headline')}</h2>
							<p className="text-sm text-gray-600">{t('trialForm.headings.step2.subheadline')}</p>
						</div>
					)}
					<TrialFields
						formId={skuValue}
						ctaText={currentStep === 1 ? form1CTAText : form2CTAText}
						handleOnSubmit={currentStep === 1 ? handleOnSubmitStep1 : handleOnSubmitStep2}
					/>
					{currentStep === 1 && <div className="w-full pt-4">{legalText}</div>}
				</>
			)}
			{isLoading && currentStep === 1 && <TrialLoading title={loadingText} />}

			{showRedirectMessage && <TrialExistingAccount />}

			{showCuiMessage && <TrialCui />}

			<input
				type="text"
				name="talon6"
				id="talon6"
				defaultValue={talon}
				style={{ display: 'none' }}
			/>
			<EHawkTalonLoadScript eHawkTalonLoaded={(res) => setTalon(res)} />
		</div>
	)
}
